import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ExternalLink, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { Badge, ErrorState, LoadingState, useConfirm } from '@/components/primitives';
import { adminApi, toastError, useAdminQuery, useBusy } from '@/pages/admin/adminApi';
import { ago } from '@/pages/dashboard/format';
import table from '@/lib/countries.json';
import { SourceTag, SourceTags, TypeBadge } from '@/pages/actors/actorUi';
import '@/styles/actors.css';

/*
 * Edit one threat actor profile. The form holds only the analyst layer: a field the analyst
 * has not set shows the ATT&CK or derived value it falls back to, and "Revert" removes the
 * override. Saving sends only what changed. Suggestions from reporting are decided one by one.
 */

const COUNTRIES = table.countries.map((c) => ({ a2: c.a2, name: c.name })).sort((a, b) => a.name.localeCompare(b.name));
const COUNTRY_NAME = Object.fromEntries(COUNTRIES.map((c) => [c.a2, c.name]));
const SCALARS = ['name', 'type', 'summary', 'description', 'origin_country', 'sponsor', 'first_seen', 'sophistication', 'attribution_confidence', 'activity', 'analyst_note'];
const LISTS = ['aliases', 'aliases_removed', 'motivations', 'target_sectors', 'target_countries', 'target_regions', 'techniques', 'techniques_removed', 'software', 'software_removed', 'references'];

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function useFallback(view) {
  return useMemo(() => {
    if (!view) return () => undefined;
    const m = view.layers.mitre || {};
    const d = view.layers.derived || {};
    return (field) => {
      if (field === 'name') return m.name;
      if (field === 'description') return m.description;
      const v = d[field];
      return v === null || v === undefined || (Array.isArray(v) && !v.length) ? undefined : v;
    };
  }, [view]);
}

function Field({ label, children, curated, onRevert, fallbackSource, hint }) {
  return (
    <div className="ac-field">
      <span className="ac-field-l">
        {label}
        {curated ? <SourceTag source="analyst" /> : fallbackSource ? <SourceTag source={fallbackSource} /> : null}
        {curated && onRevert && <button type="button" className="ac-revert" onClick={onRevert} title="Remove the analyst value"><RotateCcw aria-hidden="true" /> Revert</button>}
      </span>
      {children}
      {hint && <span className="ac-fine">{hint}</span>}
    </div>
  );
}

function Toggles({ options, value, onChange }) {
  const set = new Set(value || []);
  return (
    <div className="ac-toggles">
      {options.map((o) => {
        const id = o.id ?? o;
        const on = set.has(id);
        return (
          <button key={id} type="button" aria-pressed={on} className="ac-toggle"
            onClick={() => { const n = new Set(set); if (on) n.delete(id); else n.add(id); onChange(options.map((x) => x.id ?? x).filter((x) => n.has(x))); }}>
            {on && <Check aria-hidden="true" />}{o.label ?? o}
          </button>
        );
      })}
    </div>
  );
}

function Picker({ kind, onPick, placeholder }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      adminApi.get('/admin/attack/search', { params: { q, kind } }).then((r) => live && setResults(r.data.results)).catch(() => live && setResults([]));
    }, 160);
    return () => { live = false; clearTimeout(t); };
  }, [q, kind]);
  return (
    <div className="ac-picker">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} aria-label={placeholder} className="ac-input" data-testid={`picker-${kind}`} />
      {results.length > 0 && (
        <ul className="ac-picker-list" role="listbox">
          {results.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => { onPick(r); setQ(''); setResults([]); }}>
                <code className="ac-tid">{r.id}</code> {r.name}
                <span className="ac-muted">{kind === 'technique' ? r.tactics.join(', ') : r.type}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Suggestions({ items, disabled, onDecide, busy }) {
  if (!items.length) return <p className="ac-muted">Nothing to review. Suggestions appear when attributed stories show something the profile lacks.</p>;
  return (
    <ul className="ac-sugs">
      {items.map((s) => (
        <li key={s.key} data-testid={`sug-${s.key}`}>
          <div className="ac-sug-main">
            <span className="ac-sug-kind">{{ technique: 'Technique', malware: 'Malware', target_country: 'Country', target_sector: 'Sector' }[s.kind]}</span>
            <b>{s.label}</b>
            <span className="ac-muted">{s.count} attributed {s.count === 1 ? 'story' : 'stories'}{s.detail ? ` · ${s.detail}` : ''}</span>
          </div>
          <div className="ac-sug-actions">
            <button type="button" className="is-btn is-btn--primary is-btn--sm" disabled={disabled || busy(s.key)} onClick={() => onDecide(s, 'accepted')}>Add</button>
            <button type="button" className="is-btn is-btn--ghost is-btn--sm" disabled={disabled || busy(s.key)} onClick={() => onDecide(s, 'rejected')}>Reject</button>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminActorEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const res = useAdminQuery(() => adminApi.get(`/admin/actors/${id}`).then((r) => r.data), [id]);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({});
  const [names, setNames] = useState({});
  const [aliasInput, setAliasInput] = useState('');
  const [countryInput, setCountryInput] = useState('');
  const [customSw, setCustomSw] = useState({ name: '', type: 'malware' });
  const { run, busy } = useBusy();
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => { if (res.data) { setView(res.data); setForm(structuredClone(res.data.layers.curated || {})); } }, [res.data]);
  const fallback = useFallback(view);

  const original = view?.layers.curated || {};
  const changed = useMemo(() => [...SCALARS, ...LISTS].filter((f) => !same(form[f], original[f])), [form, original]);
  const dirty = changed.length > 0;

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (res.loading && !view) return <div className="pg ac"><LoadingState label="profile" rows={8} /></div>;
  if (res.error && !view) return <div className="pg ac"><ErrorState title="Couldn't load this profile" error={res.error} onRetry={res.reload} /></div>;
  if (!view) return null;

  const p = view.profile;
  const v = view.vocab;
  const setF = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const revert = (field) => setForm((f) => { const n = { ...f }; delete n[field]; return n; });
  const val = (field) => (form[field] !== undefined ? form[field] : fallback(field));
  const fbSource = (field) => (form[field] !== undefined ? null : field === 'name' || field === 'description' ? (view.layers.mitre ? 'mitre' : null) : fallback(field) !== undefined ? 'derived' : null);
  const apply = (data, message) => { setView(data); setForm(structuredClone(data.layers.curated || {})); if (message) toast.success(message); };

  const save = () => run('save', async () => {
    const body = {};
    changed.forEach((f) => { body[f] = form[f] === undefined || form[f] === '' ? null : form[f]; });
    try {
      const r = await adminApi.put(`/admin/actors/${p.id}`, body);
      apply(r.data, `Saved ${changed.length} ${changed.length === 1 ? 'change' : 'changes'}`);
    } catch (err) {
      toastError(err, "Couldn't save the profile");
    }
  });

  const setStatus = (status) => run('status', async () => {
    try {
      const r = await adminApi.put(`/admin/actors/${p.id}`, { status });
      apply(r.data, status === 'published' ? 'Published: customers can see this profile' : status === 'archived' ? 'Archived: hidden from customers' : 'Moved to draft: hidden from customers');
    } catch (err) {
      toastError(err, "Couldn't change the status");
    }
  });

  const decide = (s, decision) => run(s.key, async () => {
    try {
      const r = await adminApi.post(`/admin/actors/${p.id}/suggestions`, { key: s.key, decision });
      apply(r.data, decision === 'accepted' ? `Added ${s.label}` : `Rejected ${s.label}`);
    } catch (err) {
      toastError(err, "Couldn't record the decision");
    }
  });

  const remove = async () => {
    const ok = await confirm({ title: `Delete ${p.name}?`, description: 'The profile and its analyst notes are removed. Stories that name the actor stay as they are.', confirmLabel: 'Delete profile', destructive: true });
    if (!ok) return;
    run('delete', async () => {
      try {
        await adminApi.delete(`/admin/actors/${p.id}`);
        toast.success(`${p.name} deleted`);
        navigate('/admin/actors');
      } catch (err) {
        toastError(err, "Couldn't delete the profile");
      }
    });
  };

  // Aliases: ATT&CK's, plus added, minus removed
  const mitreAliases = view.layers.mitre?.aliases || [];
  const removedAliases = form.aliases_removed || [];
  const addedAliases = form.aliases || [];
  const addAlias = () => {
    const a = aliasInput.trim();
    if (!a) return;
    if (mitreAliases.some((x) => x.toLowerCase() === a.toLowerCase())) setF('aliases_removed', removedAliases.filter((x) => x.toLowerCase() !== a.toLowerCase()));
    else if (!addedAliases.some((x) => x.toLowerCase() === a.toLowerCase())) setF('aliases', [...addedAliases, a]);
    setAliasInput('');
  };

  // Techniques: the saved profile, adjusted by unsaved edits
  const removedT = new Set(form.techniques_removed || []);
  const curatedT = form.techniques || [];
  const savedIds = new Set(p.techniques.map((t) => t.id));
  const techRows = [
    ...p.techniques.map((t) => ({ ...t, removed: removedT.has(t.id) || (!t.sources.includes('mitre') && !curatedT.some((c) => c.id === t.id)), note: (curatedT.find((c) => c.id === t.id) || {}).note ?? t.note })),
    ...curatedT.filter((c) => !savedIds.has(c.id)).map((c) => ({ id: c.id, full_name: names[c.id] || c.id, sources: [c.source], tactics: [], note: c.note, pending: true })),
  ];
  const removedMitre = [...removedT].filter((tid) => !savedIds.has(tid));
  const toggleTech = (t) => {
    const isMitre = t.sources?.includes('mitre');
    if (t.removed) {
      setF('techniques_removed', (form.techniques_removed || []).filter((x) => x !== t.id));
      if (!isMitre && !curatedT.some((c) => c.id === t.id)) setF('techniques', [...curatedT, { id: t.id, source: 'analyst', note: t.note || null }]);
    } else {
      setF('techniques', curatedT.filter((c) => c.id !== t.id));
      if (isMitre) setF('techniques_removed', [...(form.techniques_removed || []), t.id]);
    }
  };
  const noteTech = (t, note) => {
    const exists = curatedT.some((c) => c.id === t.id);
    setF('techniques', exists ? curatedT.map((c) => (c.id === t.id ? { ...c, note: note || null } : c)) : [...curatedT, { id: t.id, source: 'analyst', note: note || null }]);
  };

  // Software
  const removedS = new Set(form.software_removed || []);
  const curatedS = form.software || [];
  const keyOf = (s) => s.id || `custom:${String(s.name || '').trim().toLowerCase()}`;
  const savedKeys = new Set(p.software.map((s) => s.key));
  const swRows = [
    ...p.software.map((s) => ({ ...s, removed: removedS.has(s.key) || (!s.sources.includes('mitre') && !curatedS.some((c) => keyOf(c) === s.key)) })),
    ...curatedS.filter((c) => !savedKeys.has(keyOf(c))).map((c) => ({ key: keyOf(c), id: c.id, name: names[c.id] || c.name || c.id, type: c.type, sources: [c.source], pending: true })),
  ];
  const toggleSw = (s) => {
    const isMitre = s.sources?.includes('mitre');
    if (s.removed) {
      setF('software_removed', (form.software_removed || []).filter((x) => x !== s.key));
      if (!isMitre && !curatedS.some((c) => keyOf(c) === s.key)) setF('software', [...curatedS, { id: s.id || null, name: s.id ? null : s.name, type: s.type, source: 'analyst' }]);
    } else {
      setF('software', curatedS.filter((c) => keyOf(c) !== s.key));
      if (isMitre) setF('software_removed', [...(form.software_removed || []), s.key]);
    }
  };
  const refs = form.references || [];
  const countries = val('target_countries') || [];

  return (
    <div className="pg ac ac-editor" data-testid="actor-editor">
      <header className="ac-head">
        <div className="ac-head-main">
          <p className="ac-fine"><Link to="/admin/actors">Threat actor profiles</Link></p>
          <div className="ac-head-title">
            <h1 className="t-title">{p.name}</h1>
            <Badge tone={{ published: 'success', draft: 'warning', archived: 'neutral' }[p.status]}>{p.status}</Badge>
            <TypeBadge type={p.type} label={p.type_label} />
            {p.mitre?.id && <a className="ac-mitre" href={p.mitre.url} target="_blank" rel="noopener noreferrer">{p.mitre.id} <ExternalLink aria-hidden="true" /></a>}
          </div>
          <p className="ac-fine">
            {p.origin_kind === 'mitre' ? `From ATT&CK v${p.mitre?.version}` : 'Analyst profile'}
            {view.layers.derived ? `, attributes derived ${ago(view.layers.derived.derived_at)}` : ''}. Updated {ago(p.updated_at)}.
          </p>
        </div>
        <div className="ac-head-actions">
          {p.status === 'published' && <Link to={`/actors/${p.slug}`} target="_blank" className="is-btn is-btn--ghost is-btn--sm">View as customer <ExternalLink aria-hidden="true" /></Link>}
          {p.status !== 'published' && <button type="button" className="is-btn is-btn--primary is-btn--sm" disabled={dirty || busy('status')} onClick={() => setStatus('published')} data-testid="publish">Publish</button>}
          {p.status === 'published' && <button type="button" className="is-btn is-btn--secondary is-btn--sm" disabled={dirty || busy('status')} onClick={() => setStatus('draft')}>Unpublish</button>}
          {p.status !== 'archived' && <button type="button" className="is-btn is-btn--ghost is-btn--sm" disabled={dirty || busy('status')} onClick={() => setStatus('archived')}>Archive</button>}
          {p.origin_kind === 'analyst' && <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={remove} aria-label="Delete profile"><Trash2 aria-hidden="true" /></button>}
        </div>
      </header>

      <div className="ac-edit-grid">
        <div className="ac-col">
          <section className="ac-card">
            <h2 className="ac-h2">Identity</h2>
            <Field label="Name" curated={form.name !== undefined} onRevert={view.layers.mitre ? () => revert('name') : null} fallbackSource={fbSource('name')}>
              <input className="ac-input" value={val('name') || ''} onChange={(e) => setF('name', e.target.value)} maxLength={120} data-testid="f-name" />
            </Field>
            <Field label="Aliases" hint="Stories that name any alias link to this profile.">
              <div className="ac-chips ac-chips--edit">
                {mitreAliases.map((a) => {
                  const off = removedAliases.some((x) => x.toLowerCase() === a.toLowerCase());
                  return (
                    <button key={a} type="button" className={`ac-chip ac-chip--btn${off ? ' is-off' : ''}`} title={off ? 'Restore this ATT&CK alias' : 'Remove this ATT&CK alias'}
                      onClick={() => setF('aliases_removed', off ? removedAliases.filter((x) => x.toLowerCase() !== a.toLowerCase()) : [...removedAliases, a])}>
                      {a}{off ? <RotateCcw aria-hidden="true" /> : <X aria-hidden="true" />}
                    </button>
                  );
                })}
                {addedAliases.map((a) => (
                  <button key={a} type="button" className="ac-chip ac-chip--btn is-analyst" onClick={() => setF('aliases', addedAliases.filter((x) => x !== a))} title="Remove">{a}<X aria-hidden="true" /></button>
                ))}
              </div>
              <div className="ac-inline">
                <input className="ac-input" value={aliasInput} onChange={(e) => setAliasInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }} placeholder="Add an alias" maxLength={80} />
                <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={addAlias} disabled={!aliasInput.trim()}>Add</button>
              </div>
            </Field>
            <Field label="Type" curated={form.type !== undefined} onRevert={() => revert('type')} fallbackSource={fbSource('type')}>
              <select className="ac-input" value={val('type') || ''} onChange={(e) => setF('type', e.target.value || undefined)}>
                <option value="">Not set</option>{v.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Summary" curated={form.summary !== undefined} onRevert={() => revert('summary')} fallbackSource={fbSource('summary')} hint="One or two sentences shown at the top of the profile and in lists.">
              <textarea className="ac-input" rows={3} maxLength={400} value={val('summary') || ''} onChange={(e) => setF('summary', e.target.value)} />
            </Field>
            <Field label="Description" curated={form.description !== undefined} onRevert={view.layers.mitre ? () => revert('description') : null} fallbackSource={fbSource('description')}>
              <textarea className="ac-input" rows={8} maxLength={12000} value={val('description') || ''} onChange={(e) => setF('description', e.target.value)} />
            </Field>
          </section>

          <section className="ac-card">
            <h2 className="ac-h2">Attribution</h2>
            <div className="ac-form-grid">
              <Field label="Origin country" curated={form.origin_country !== undefined} onRevert={() => revert('origin_country')} fallbackSource={fbSource('origin_country')}>
                <select className="ac-input" value={val('origin_country') || ''} onChange={(e) => setF('origin_country', e.target.value || undefined)}>
                  <option value="">Not attributed</option>{COUNTRIES.map((c) => <option key={c.a2} value={c.a2}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Sponsor or affiliation" curated={form.sponsor !== undefined} onRevert={() => revert('sponsor')} fallbackSource={fbSource('sponsor')}>
                <input className="ac-input" value={val('sponsor') || ''} onChange={(e) => setF('sponsor', e.target.value)} maxLength={160} placeholder="Named agency or unit, if attributed" />
              </Field>
              <Field label="Active since" curated={form.first_seen !== undefined} onRevert={() => revert('first_seen')} fallbackSource={fbSource('first_seen')}>
                <input className="ac-input" type="number" min={1980} max={2100} value={val('first_seen') || ''} onChange={(e) => setF('first_seen', e.target.value ? Number(e.target.value) : undefined)} />
              </Field>
              <Field label="Activity" curated={form.activity !== undefined} onRevert={() => revert('activity')}>
                <select className="ac-input" value={form.activity || ''} onChange={(e) => setF('activity', e.target.value || undefined)}>
                  <option value="">Not assessed</option><option value="active">Active</option><option value="dormant">Dormant</option>
                </select>
              </Field>
              <Field label="Sophistication" curated={form.sophistication !== undefined} onRevert={() => revert('sophistication')}>
                <select className="ac-input" value={form.sophistication || ''} onChange={(e) => setF('sophistication', e.target.value || undefined)}>
                  <option value="">Not assessed</option>{v.sophistication.map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
              <Field label="Attribution confidence" curated={form.attribution_confidence !== undefined} onRevert={() => revert('attribution_confidence')}>
                <select className="ac-input" value={form.attribution_confidence || ''} onChange={(e) => setF('attribution_confidence', e.target.value || undefined)}>
                  <option value="">Not assessed</option>{v.confidence.map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Motivation" curated={form.motivations !== undefined} onRevert={() => revert('motivations')} fallbackSource={fbSource('motivations')}>
              <Toggles options={v.motivations} value={val('motivations')} onChange={(x) => setF('motivations', x)} />
            </Field>
          </section>

          <section className="ac-card">
            <h2 className="ac-h2">Targeting</h2>
            <Field label="Sectors" curated={form.target_sectors !== undefined} onRevert={() => revert('target_sectors')} fallbackSource={fbSource('target_sectors')}>
              <Toggles options={v.sectors} value={val('target_sectors')} onChange={(x) => setF('target_sectors', x)} />
            </Field>
            <Field label="Countries" curated={form.target_countries !== undefined} onRevert={() => revert('target_countries')} fallbackSource={fbSource('target_countries')}>
              <div className="ac-chips ac-chips--edit">
                {countries.map((c) => <button key={c} type="button" className="ac-chip ac-chip--btn" onClick={() => setF('target_countries', countries.filter((x) => x !== c))}>{COUNTRY_NAME[c] || c}<X aria-hidden="true" /></button>)}
                {!countries.length && <span className="ac-muted">None stated</span>}
              </div>
              <select className="ac-input" value={countryInput} onChange={(e) => { const c = e.target.value; if (c && !countries.includes(c)) setF('target_countries', [...countries, c]); setCountryInput(''); }} aria-label="Add a targeted country">
                <option value="">Add a country</option>{COUNTRIES.filter((c) => !countries.includes(c.a2)).map((c) => <option key={c.a2} value={c.a2}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Regions" curated={form.target_regions !== undefined} onRevert={() => revert('target_regions')} fallbackSource={fbSource('target_regions')}>
              <Toggles options={v.regions} value={val('target_regions')} onChange={(x) => setF('target_regions', x)} />
            </Field>
          </section>

          <section className="ac-card">
            <div className="ac-card-h">
              <h2 className="ac-h2">Techniques <span className="ac-h2-n">{techRows.filter((t) => !t.removed).length}</span></h2>
              <Picker kind="technique" placeholder="Add a technique: ID or name" onPick={(r) => {
                setNames((n) => ({ ...n, [r.id]: r.name }));
                setF('techniques_removed', (form.techniques_removed || []).filter((x) => x !== r.id));
                if (!curatedT.some((c) => c.id === r.id) && !(savedIds.has(r.id) && !removedT.has(r.id))) setF('techniques', [...curatedT, { id: r.id, source: 'analyst', note: null }]);
              }} />
            </div>
            <ul className="ac-edit-list">
              {techRows.map((t) => (
                <li key={t.id} className={t.removed ? 'is-removed' : t.pending ? 'is-pending' : undefined}>
                  <div className="ac-edit-row">
                    <code className="ac-tid">{t.id}</code>
                    <span className="ac-tname">{t.full_name}</span>
                    <SourceTags sources={t.sources} showMitre />
                    {t.pending && <span className="ac-unsaved">Unsaved</span>}
                    <button type="button" className="ac-icon-btn" onClick={() => toggleTech(t)} aria-label={t.removed ? `Restore ${t.id}` : `Remove ${t.id}`}>{t.removed ? <RotateCcw aria-hidden="true" /> : <X aria-hidden="true" />}</button>
                  </div>
                  {!t.removed && (
                    <input className="ac-input ac-input--note" defaultValue={t.note || ''} placeholder="Analyst note on how this group uses it (optional)" maxLength={500}
                      onBlur={(e) => { if ((e.target.value || null) !== (t.note || null)) noteTech(t, e.target.value.trim()); }} />
                  )}
                </li>
              ))}
              {removedMitre.map((tid) => <li key={tid} className="is-removed"><div className="ac-edit-row"><code className="ac-tid">{tid}</code><span className="ac-tname">Removed</span>
                <button type="button" className="ac-icon-btn" onClick={() => setF('techniques_removed', (form.techniques_removed || []).filter((x) => x !== tid))} aria-label={`Restore ${tid}`}><RotateCcw aria-hidden="true" /></button></div></li>)}
            </ul>
          </section>

          <section className="ac-card">
            <div className="ac-card-h">
              <h2 className="ac-h2">Malware and tools <span className="ac-h2-n">{swRows.filter((s) => !s.removed).length}</span></h2>
              <Picker kind="software" placeholder="Add from ATT&CK: name or alias" onPick={(r) => {
                setNames((n) => ({ ...n, [r.id]: r.name }));
                setF('software_removed', (form.software_removed || []).filter((x) => x !== r.id));
                if (!curatedS.some((c) => c.id === r.id) && !(savedKeys.has(r.id) && !removedS.has(r.id))) setF('software', [...curatedS, { id: r.id, type: r.type, source: 'analyst' }]);
              }} />
            </div>
            <div className="ac-inline">
              <input className="ac-input" value={customSw.name} onChange={(e) => setCustomSw((c) => ({ ...c, name: e.target.value }))} placeholder="Or name software ATT&CK does not have" maxLength={80} />
              <select className="ac-input ac-input--narrow" value={customSw.type} onChange={(e) => setCustomSw((c) => ({ ...c, type: e.target.value }))} aria-label="Software type"><option value="malware">Malware</option><option value="tool">Tool</option></select>
              <button type="button" className="is-btn is-btn--secondary is-btn--sm" disabled={!customSw.name.trim()} onClick={() => { setF('software', [...curatedS, { id: null, name: customSw.name.trim(), type: customSw.type, source: 'analyst' }]); setCustomSw({ name: '', type: 'malware' }); }}><Plus aria-hidden="true" /> Add</button>
            </div>
            <ul className="ac-edit-list">
              {swRows.map((s) => (
                <li key={s.key} className={s.removed ? 'is-removed' : s.pending ? 'is-pending' : undefined}>
                  <div className="ac-edit-row">
                    {s.id ? <code className="ac-tid">{s.id}</code> : <span className="ac-tid">Custom</span>}
                    <span className="ac-tname">{s.name}</span>
                    <span className={`ac-swtype ac-swtype--${s.type}`}>{s.type === 'tool' ? 'Tool' : 'Malware'}</span>
                    <SourceTags sources={s.sources} showMitre />
                    {s.pending && <span className="ac-unsaved">Unsaved</span>}
                    <button type="button" className="ac-icon-btn" onClick={() => toggleSw(s)} aria-label={s.removed ? `Restore ${s.name}` : `Remove ${s.name}`}>{s.removed ? <RotateCcw aria-hidden="true" /> : <X aria-hidden="true" />}</button>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="ac-card">
            <div className="ac-card-h">
              <h2 className="ac-h2">References added by analysts <span className="ac-h2-n">{refs.length}</span></h2>
              <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => setF('references', [...refs, { title: '', url: '' }])}><Plus aria-hidden="true" /> Add reference</button>
            </div>
            {refs.length === 0 && <p className="ac-fine">ATT&CK supplies {p.references.length} references. Add your own reporting or vendor research here; they are listed first.</p>}
            {refs.map((r, i) => (
              <div key={i} className="ac-inline">
                <input className="ac-input" value={r.title} placeholder="Title" maxLength={300} onChange={(e) => setF('references', refs.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
                <input className="ac-input" value={r.url || ''} placeholder="https://" maxLength={600} onChange={(e) => setF('references', refs.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} />
                <button type="button" className="ac-icon-btn" onClick={() => setF('references', refs.filter((_, j) => j !== i))} aria-label="Remove reference"><X aria-hidden="true" /></button>
              </div>
            ))}
          </section>

          <section className="ac-card">
            <Field label="Internal analyst note" curated={form.analyst_note !== undefined} onRevert={() => revert('analyst_note')} hint="Visible to IntelliSecure analysts only, never to customers.">
              <textarea className="ac-input" rows={4} maxLength={4000} value={form.analyst_note || ''} onChange={(e) => setF('analyst_note', e.target.value)} />
            </Field>
          </section>
        </div>

        <aside className="ac-col ac-edit-side">
          <section className="ac-card">
            <h2 className="ac-h2">Suggested by reporting <span className="ac-h2-n">{view.suggestions.length}</span></h2>
            {dirty && view.suggestions.length > 0 && <p className="ac-fine">Save or discard your edits before deciding on suggestions.</p>}
            <Suggestions items={view.suggestions} disabled={dirty} onDecide={decide} busy={busy} />
          </section>
          <section className="ac-card">
            <h2 className="ac-h2">Our reporting</h2>
            <dl className="ac-facts">
              <div className="ac-fact"><dt>Attributed stories</dt><dd>{view.insights.counts.attributed}</dd></div>
              <div className="ac-fact"><dt>Mentioned in</dt><dd>{view.insights.counts.mentioned}</dd></div>
              <div className="ac-fact"><dt>Last reported</dt><dd>{view.insights.last_reported ? ago(view.insights.last_reported) : 'Never'}</dd></div>
              <div className="ac-fact"><dt>Indicators</dt><dd>{view.insights.indicators.length}</dd></div>
            </dl>
            {view.insights.stories.length > 0 && (
              <ul className="ac-unprof-stories">{view.insights.stories.slice(0, 8).map((s) => <li key={s.id}>{s.name} <span className="ac-muted">({s.relation})</span></li>)}</ul>
            )}
          </section>
          {view.layers.derived && (
            <section className="ac-card">
              <h2 className="ac-h2">Derived from the ATT&CK text</h2>
              <p className="ac-fine">Read by {view.layers.derived.model} {ago(view.layers.derived.derived_at)}. Used wherever no analyst value is set; confirm by setting the field.</p>
            </section>
          )}
        </aside>
      </div>

      {dirty && (
        <div className="ac-savebar" role="region" aria-label="Unsaved changes">
          <span>{changed.length} unsaved {changed.length === 1 ? 'change' : 'changes'}: {changed.join(', ').replaceAll('_', ' ')}</span>
          <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={() => setForm(structuredClone(original))}>Discard</button>
          <button type="button" className="is-btn is-btn--primary is-btn--sm" onClick={save} disabled={busy('save')} data-testid="save">{busy('save') ? 'Saving…' : 'Save changes'}</button>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
